import React from 'react';
import {
  Box, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  CircularProgress, Stack, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import { TEAM_STATUS_CHOICES } from 'Includes/teamStatus';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const FIELDS = [
  { category: 'staff', item: 'tag', group: 'Staff', label: 'Tag' },
  { category: 'staff', item: 'card', group: 'Staff', label: 'Card' },
  { category: 'staff', item: 'full_service', group: 'Staff', label: 'Full service' },
  { category: 'student', item: 'tag', group: 'Student', label: 'Tag' },
  { category: 'student', item: 'card', group: 'Student', label: 'Card' },
  { category: 'student', item: 'full_service', group: 'Student', label: 'Full service' },
];

const fieldKey = (field) => `${field.category}.${field.item}`;

const formatAmount = (value) => {
  const amount = parseFloat(value || 0);
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatHeadcount = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const count = Number(value);
  if (!Number.isFinite(count)) return '—';
  return count.toLocaleString('en-IN');
};

const lineAmount = (count, price) => {
  const qty = parseFloat(count);
  const rate = parseFloat(price);
  if (!qty || qty <= 0 || Number.isNaN(rate) || rate < 0) return 0;
  return qty * rate;
};

const billedAmount = (row, cells) => {
  const base = FIELDS.reduce((sum, field) => {
    const cell = cells[fieldKey(field)] || {};
    return sum + lineAmount(cell.count, cell.unit_price);
  }, 0);
  if (!row?.has_gst) return base;
  const gst = parseFloat(row.gst_percentage) || 0;
  return base + (base * gst / 100);
};

const draftsFromRows = (rows) => {
  const next = {};
  (rows || []).forEach((row) => {
    const cells = {};
    FIELDS.forEach((field) => {
      const cell = row.items?.[field.category]?.[field.item] || {};
      cells[fieldKey(field)] = {
        count: cell.count ?? '',
        unit_price: cell.unit_price ?? '',
      };
    });
    next[row.order_id] = cells;
  });
  return next;
};

const canEditOrders = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.is_superuser) return true;
    const codes = Array.isArray(user.permission_codenames) ? user.permission_codenames : [];
    return codes.includes('visible_orders_v2_change');
  } catch (err) {
    return false;
  }
};

function IdCardTracker() {
  const navigate = useNavigate();
  const editable = React.useMemo(() => canEditOrders(), []);
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [drafts, setDrafts] = React.useState({});
  const [summary, setSummary] = React.useState(null);
  const [yearList, setYearList] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [yearsReady, setYearsReady] = React.useState(false);
  const [statusByTeam, setStatusByTeam] = React.useState('all');
  const [searchInput, setSearchInput] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [savingId, setSavingId] = React.useState(null);
  const [tableMaxHeight, setTableMaxHeight] = React.useState(520);
  const [headerRowHeight, setHeaderRowHeight] = React.useState(41);
  const searchTimeoutRef = React.useRef(null);
  const tableContainerRef = React.useRef(null);
  const firstHeaderRowRef = React.useRef(null);

  const fetchYears = () => {
    getRequest(GET_URL.academicyear.api).then((response) => {
      if (response && response.status === 200) {
        const list = response.data.data || response.data || [];
        setYearList(list);
        const activeYear = list.find((year) => year.is_active || year.active);
        if (activeYear) setSelectedYear(activeYear.id);
        else if (list.length > 0) setSelectedYear(list[0].id);
        setYearsReady(true);
      }
    });
  };

  const fetchRows = React.useCallback(() => {
    setLoading(true);
    const params = {};
    if (selectedYear) params.year = selectedYear;
    if (search) params.search = search;
    if (statusByTeam && statusByTeam !== 'all') params.status_by_team = statusByTeam;
    getRequest(`${GET_URL.orders_v2.api}id_cards/`, params).then((response) => {
      if (response && response.status === 200) {
        const data = response.data.data || [];
        setRows(data);
        setDrafts(draftsFromRows(data));
        setSummary(response.data.summary || null);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selectedYear, search, statusByTeam]);

  React.useEffect(() => {
    fetchYears();
  }, []);

  React.useEffect(() => {
    if (!yearsReady) return;
    fetchRows();
  }, [yearsReady, fetchRows]);

  const handleSearch = (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(value);
    }, 400);
  };

  const updateCell = (orderId, key, prop, value) => {
    setDrafts((prev) => ({
      ...prev,
      [orderId]: {
        ...(prev[orderId] || {}),
        [key]: {
          ...((prev[orderId] || {})[key] || {}),
          [prop]: value,
        },
      },
    }));
  };

  const isDirty = (row) => {
    const original = draftsFromRows([row])[row.order_id];
    const current = drafts[row.order_id] || {};
    return FIELDS.some((field) => {
      const key = fieldKey(field);
      const left = original[key] || {};
      const right = current[key] || {};
      return String(left.count ?? '') !== String(right.count ?? '')
        || String(left.unit_price ?? '') !== String(right.unit_price ?? '');
    });
  };

  const handleSave = async (row) => {
    const cells = drafts[row.order_id] || {};
    setSavingId(row.order_id);
    try {
      const response = await postRequest(
        `${POST_URL.orders_v2.api}${row.order_id}/update_id_card/`,
        {
          items: FIELDS.map((field) => {
            const cell = cells[fieldKey(field)] || {};
            return {
              category: field.category,
              item: field.item,
              count: cell.count === '' ? 0 : cell.count,
              unit_price: cell.unit_price === '' ? 0 : cell.unit_price,
            };
          }),
        },
      );
      if (response && response.status === 200 && response.data) {
        const updated = response.data;
        setRows((prev) => prev.map((item) => (item.order_id === updated.order_id ? updated : item)));
        setDrafts((prev) => ({ ...prev, [updated.order_id]: draftsFromRows([updated])[updated.order_id] }));
        Swal.fire({ icon: 'success', title: 'ID card updated', timer: 1200, showConfirmButton: false });
        fetchRows();
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not update', text: 'Check the count and price, then try again.' });
    }
    setSavingId(null);
  };

  const liveSummary = React.useMemo(() => {
    const totals = {
      schools: rows.length,
      staff_tag_count: 0,
      staff_card_count: 0,
      student_tag_count: 0,
      student_card_count: 0,
      amount: 0,
      paid_amount: 0,
    };
    rows.forEach((row) => {
      const cells = drafts[row.order_id] || {};
      totals.amount += billedAmount(row, cells);
      totals.paid_amount += parseFloat(row.paid_amount) || 0;
      [
        ['staff.tag', 'staff_tag_count'],
        ['staff.card', 'staff_card_count'],
        ['student.tag', 'student_tag_count'],
        ['student.card', 'student_card_count'],
      ].forEach(([key, bucket]) => {
        totals[bucket] += parseFloat(cells[key]?.count) || 0;
      });
    });
    return totals;
  }, [rows, drafts]);

  React.useLayoutEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return undefined;

    const update = () => {
      const top = container.getBoundingClientRect().top;
      const nextHeight = Math.max(280, Math.floor(window.innerHeight - top - 36));
      setTableMaxHeight((prev) => (prev === nextHeight ? prev : nextHeight));

      const headerRow = firstHeaderRowRef.current;
      if (!headerRow) return;
      const nextHeader = headerRow.getBoundingClientRect().height;
      if (nextHeader > 0) {
        setHeaderRowHeight((prev) => (Math.abs(prev - nextHeader) < 0.5 ? prev : nextHeader));
      }
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [loading, rows.length]);

  const summaryCards = [
    { label: 'Schools', value: liveSummary.schools, money: false },
    { label: 'Staff tags', value: liveSummary.staff_tag_count, money: false },
    { label: 'Staff cards', value: liveSummary.staff_card_count, money: false },
    { label: 'Student tags', value: liveSummary.student_tag_count, money: false },
    { label: 'Student cards', value: liveSummary.student_card_count, money: false },
    { label: 'ID card amount', value: liveSummary.amount, money: true },
    { label: 'Paid', value: liveSummary.paid_amount, money: true },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
            ID Card Orders
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: 13, mt: 0.2 }}>
            Staff and student cards and tags for every school. Update the count and price here.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate('/billing/orders-v2/list')}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, borderColor: '#cbd5e1', color: '#334155' }}
        >
          Back to Orders
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <TextField
          size="small"
          placeholder="Search school, code, or order number"
          value={searchInput}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 260px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160, bgcolor: '#fff' }}>
          <InputLabel>Academic Year</InputLabel>
          <Select
            value={selectedYear}
            label="Academic Year"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {yearList.map((year) => (
              <MenuItem key={year.id} value={year.id}>{year.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Client Status</InputLabel>
          <Select
            value={statusByTeam}
            label="Client Status"
            onChange={(e) => setStatusByTeam(e.target.value)}
          >
            {TEAM_STATUS_CHOICES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' }, gap: 1.5, mb: 2 }}>
        {summaryCards.map((card) => (
          <Box key={card.label} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {card.label}
            </Typography>
            <Typography sx={{ mt: 0.4, fontWeight: 800, fontSize: 18, color: '#0f172a' }}>
              {card.money ? formatAmount(card.value) : Number(card.value || 0).toLocaleString('en-IN')}
            </Typography>
          </Box>
        ))}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ color: '#94a3b8' }}>No ID card orders for this filter</Typography>
        </Paper>
      ) : (
        <TableContainer
          ref={tableContainerRef}
          component={Paper}
          sx={{
            borderRadius: 2.5,
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            maxHeight: tableMaxHeight,
            overflow: 'auto',
          }}
        >
          <Table size="small" sx={{ minWidth: 1380, borderCollapse: 'separate', borderSpacing: 0 }}>
            <TableHead>
              <TableRow
                ref={firstHeaderRowRef}
                sx={{
                  '& th': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    background: '#f8fafc',
                    fontWeight: 700,
                    color: '#475569',
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                  },
                  '& th:first-of-type': { left: 0, zIndex: 5, minWidth: 280 },
                }}
              >
                <TableCell rowSpan={2}>School</TableCell>
                {['Staff', 'Student'].map((group) => (
                  <TableCell key={group} align="center" colSpan={3} sx={{ borderBottom: 'none' }}>{group}</TableCell>
                ))}
                <TableCell rowSpan={2} align="right">Amount</TableCell>
                <TableCell rowSpan={2} align="right">Paid</TableCell>
                <TableCell rowSpan={2} align="center"> </TableCell>
              </TableRow>
              <TableRow
                sx={{
                  '& th': {
                    position: 'sticky',
                    top: headerRowHeight,
                    zIndex: 3,
                    background: '#f8fafc',
                    fontWeight: 700,
                    color: '#64748b',
                    fontSize: 11,
                    boxShadow: 'inset 0 -1px 0 #e2e8f0',
                  },
                }}
              >
                {FIELDS.map((field) => (
                  <TableCell key={fieldKey(field)} align="center" sx={{ whiteSpace: 'nowrap' }}>
                    {field.label}
                    <Typography component="div" sx={{ fontSize: 10, fontWeight: 500, color: '#94a3b8', textTransform: 'none', letterSpacing: 0 }}>count × price</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const cells = drafts[row.order_id] || {};
                const dirty = isDirty(row);
                const amount = billedAmount(row, cells);
                const staffCount = formatHeadcount(row.actual_staff_count);
                const studentCount = formatHeadcount(row.actual_student_count);
                return (
                  <TableRow key={row.order_id} hover>
                    <TableCell sx={{ position: 'sticky', left: 0, background: '#fff', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <Box sx={{ minWidth: 78, pt: 0.2, lineHeight: 1.25 }}>
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1d4ed8' }}>
                            Staff {staffCount}
                          </Typography>
                          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#7c3aed' }}>
                            Students {studentCount}
                          </Typography>
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: '#1e293b', cursor: 'pointer' }}
                            onClick={() => navigate(`/billing/orders-v2/form?id=${row.order_id}`)}
                          >
                            {row.company_name || '-'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, display: 'block' }}>
                            {row.company_code ? `Code: ${row.company_code}` : row.order_number}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {row.academic_year_name || '-'} · {row.order_number}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    {FIELDS.map((field) => {
                      const key = fieldKey(field);
                      const cell = cells[key] || { count: '', unit_price: '' };
                      const amountForLine = lineAmount(cell.count, cell.unit_price);
                      return (
                        <TableCell key={key} align="center" sx={{ minWidth: 150 }}>
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <TextField
                              size="small"
                              type="number"
                              placeholder="0"
                              value={cell.count}
                              disabled={!editable || savingId === row.order_id}
                              onChange={(e) => updateCell(row.order_id, key, 'count', e.target.value)}
                              inputProps={{ min: 0, step: 1 }}
                              sx={{ width: 68, '& input': { py: 0.6, fontSize: 13, textAlign: 'right' } }}
                            />
                            <TextField
                              size="small"
                              type="number"
                              placeholder="0"
                              value={cell.unit_price}
                              disabled={!editable || savingId === row.order_id}
                              onChange={(e) => updateCell(row.order_id, key, 'unit_price', e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 78, '& input': { py: 0.6, fontSize: 13, textAlign: 'right' } }}
                            />
                          </Stack>
                          <Typography variant="caption" sx={{ color: amountForLine > 0 ? '#334155' : '#cbd5e1', display: 'block', mt: 0.4 }}>
                            {formatAmount(amountForLine)}
                          </Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                      {formatAmount(amount)}
                      {row.has_gst && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                          incl. GST {row.gst_percentage}%
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap', color: '#15803d' }}>
                      {formatAmount(row.paid_amount)}
                    </TableCell>
                    <TableCell align="center">
                      {editable && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!dirty || savingId === row.order_id}
                          onClick={() => handleSave(row)}
                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1.5, boxShadow: 'none' }}
                        >
                          {savingId === row.order_id ? 'Saving' : 'Save'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {summary && !loading && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#94a3b8' }}>
          Blank count means that card or tag was not given for the school.
        </Typography>
      )}
    </Box>
  );
}

export default IdCardTracker;
